import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  createLscMember,
  createLscMemberInvite,
  getLscMemberTeamOptions,
  listLscMemberClaims,
  listLscMembers,
  reinviteLscMember,
  removeLscMember,
  restoreLscMember,
  updateLscMember,
  LscApiError,
} from '../../api';
import type { SetupWizardStep } from '../../leagueSetupWizard';
import { lscPath } from '../../routing';
import type {
  LscLeague,
  LscLeagueReadiness,
  LscMember,
  LscMemberCounts,
  LscMemberInviteSummary,
  LscMemberTeamOption,
} from '../../types';
import {
  formatMemberInviteMessage,
  leagueHasNeonSportzImport,
  liveDetectionConnectionCopy,
  memberRosterCounts,
} from '../../memberConnection';
import { copyTextToClipboard } from '../../copyToClipboard';
import { memberClaimSignInMethod } from '../../utils/memberStatus';
import { MEMBERS_FULL_PAGE_LINK_COPY, MEMBERS_SETUP_INTRO_COPY } from '../../setupMembersCopy';
import { buildMemberUpdatePayload } from '../../pages/LscMembersPage';
import {
  DEFAULT_MEMBER_STATUS_FILTER,
  emptyFilterMessage,
  filterMembersByStatus,
  type MemberStatusFilter,
} from '../../utils/memberFilter';
import {
  validateCustomTeamName,
  type MemberFormValues,
} from '../../utils/memberTeamAssignment';
import ImportedMembersPromotionPanel from '../ImportedMembersPromotionPanel';
import JoinRequestQueuePanel from '../JoinRequestQueuePanel';
import LeagueInvitePanel from '../LeagueInvitePanel';
import LeagueSetupStepCard from '../LeagueSetupStepCard';
import LoadingState from '../LoadingState';
import LscMutationErrorAlert from '../LscMutationErrorAlert';
import MemberClaimQueuePanel from '../MemberClaimQueuePanel';
import MemberFormModal from '../MemberFormModal';
import MembersTable from '../MembersTable';
import MemberStatusFilterBar from '../MemberStatusFilterBar';
import RemoveMemberConfirmModal from '../RemoveMemberConfirmModal';
import RestoreMemberModal from '../RestoreMemberModal';
import TeamAssignmentConflictBanner from '../TeamAssignmentConflictBanner';
import { parseLscMutationError, type ParsedLscMutationError } from '../../entitlementBillingErrors';

const MEMBERS_READINESS_KEYS = ['members_imported', 'twitch_linked', 'team_assignments'] as const;

const TEAM_ALREADY_CLAIMED_MESSAGE = 'That team is already assigned to another member.';

export interface MembersTeamsStepPanelProps {
  leagueId: string;
  league: LscLeague;
  wizardStep: SetupWizardStep;
  readiness: LscLeagueReadiness;
  isCommissioner: boolean;
  leagueArchived?: boolean;
  onSetupRefresh: () => void;
}

function membersAttentionItems(readiness: LscLeagueReadiness) {
  return (readiness.items ?? []).filter(
    (item) =>
      MEMBERS_READINESS_KEYS.includes(item.key as (typeof MEMBERS_READINESS_KEYS)[number]) &&
      item.status !== 'complete' &&
      item.status !== 'not_applicable'
  );
}

const MembersTeamsStepPanel: React.FC<MembersTeamsStepPanelProps> = ({
  leagueId,
  league,
  wizardStep,
  readiness,
  isCommissioner,
  leagueArchived = false,
  onSetupRefresh,
}) => {
  const [members, setMembers] = useState<LscMember[]>([]);
  const [memberCounts, setMemberCounts] = useState<LscMemberCounts | null>(null);
  const [pendingClaimMemberIds, setPendingClaimMemberIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingMember, setEditingMember] = useState<LscMember | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<MemberStatusFilter>(DEFAULT_MEMBER_STATUS_FILTER);
  const [removeTarget, setRemoveTarget] = useState<LscMember | null>(null);
  const [removing, setRemoving] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<LscMember | null>(null);
  const [restoreTeams, setRestoreTeams] = useState<LscMemberTeamOption[]>([]);
  const [restoreTeamsLoading, setRestoreTeamsLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [memberInvites, setMemberInvites] = useState<Record<string, LscMemberInviteSummary | undefined>>({});
  const [invitingMemberId, setInvitingMemberId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const attentionItems = useMemo(() => membersAttentionItems(readiness), [readiness]);

  const refreshAfterMutation = useCallback(async () => {
    await Promise.resolve(onSetupRefresh());
  }, [onSetupRefresh]);

  const loadMembers = useCallback(async () => {
    setError(null);
    try {
      const data = await listLscMembers(leagueId);
      setMembers(data.members);
      setMemberCounts(data.counts);
      if (isCommissioner) {
        try {
          const claimsData = await listLscMemberClaims(leagueId);
          const pendingIds = new Set(
            (claimsData.claims || [])
              .filter((claim) => claim.status === 'pending' && claim.member_id)
              .map((claim) => claim.member_id as string)
          );
          setPendingClaimMemberIds(pendingIds);
        } catch {
          setPendingClaimMemberIds(new Set());
        }
      }
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not load members.'));
    } finally {
      setLoading(false);
    }
  }, [isCommissioner, leagueId]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const openCreate = () => {
    setModalMode('create');
    setEditingMember(null);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (member: LscMember) => {
    setModalMode('edit');
    setEditingMember(member);
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (values: MemberFormValues) => {
    if (values.team_assignment_mode === 'custom' && !values.resolve_team_conflict) {
      const validationError = validateCustomTeamName(values.custom_team_name);
      if (validationError) {
        setFormError(validationError);
        return;
      }
    }
    setSaving(true);
    setError(null);
    setFormError(null);
    try {
      const payload = buildMemberUpdatePayload(values, modalMode);
      if (modalMode === 'create') {
        await createLscMember(leagueId, payload);
      } else if (editingMember) {
        await updateLscMember(leagueId, editingMember.id, payload);
      }
      setModalOpen(false);
      await loadMembers();
      await refreshAfterMutation();
    } catch (err) {
      if (err instanceof LscApiError && err.code === 'team_already_claimed') {
        setFormError(TEAM_ALREADY_CLAIMED_MESSAGE);
        return;
      }
      const parsed = parseLscMutationError(err, 'Could not save member.');
      setFormError(parsed.message);
      setError(parsed);
    } finally {
      setSaving(false);
    }
  };

  const rememberInvite = (memberId: string, issued: LscMemberInviteSummary) => {
    setMemberInvites((current) => ({ ...current, [memberId]: issued }));
  };

  const issuedInvite = (issued: { invite: LscMemberInviteSummary; claim_url: string }) => ({
    ...issued.invite,
    claim_url: issued.claim_url,
  });

  const copyMemberLink = async (member: LscMember) => {
    setInvitingMemberId(member.id);
    setError(null);
    try {
      const issued = issuedInvite(await createLscMemberInvite(leagueId, member.id));
      rememberInvite(member.id, issued);
      const copied = await copyTextToClipboard(issued.claim_url || '');
      setToast(copied ? 'Invite link copied.' : 'Invite link is ready.');
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not create an invite link.'));
    } finally {
      setInvitingMemberId(null);
    }
  };

  const copyMemberMessage = async (member: LscMember) => {
    setInvitingMemberId(member.id);
    setError(null);
    try {
      const existing = memberInvites[member.id];
      const issued = existing?.claim_url
        ? existing
        : issuedInvite(await createLscMemberInvite(leagueId, member.id));
      if (!issued.claim_url) return;
      rememberInvite(member.id, issued);
      const message = formatMemberInviteMessage({
        displayName: member.display_name,
        leagueName: league.name,
        claimUrl: issued.claim_url,
        teamLabel: member.current_side_label,
        signInMethod: memberClaimSignInMethod(member),
      });
      const copied = await copyTextToClipboard(message);
      setToast(copied ? 'Invite message copied.' : 'Invite message is ready.');
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not create an invite message.'));
    } finally {
      setInvitingMemberId(null);
    }
  };

  const reinviteMember = async (member: LscMember) => {
    setInvitingMemberId(member.id);
    setError(null);
    try {
      const issued = issuedInvite(await reinviteLscMember(leagueId, member.id));
      rememberInvite(member.id, issued);
      const copied = await copyTextToClipboard(issued.claim_url || '');
      setToast(copied ? 'New invite link copied.' : 'New invite link is ready.');
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not re-invite this member.'));
    } finally {
      setInvitingMemberId(null);
    }
  };

  const openRestore = async (member: LscMember) => {
    setRestoreTarget(member);
    setRestoreTeams([]);
    if ((member.current_side_label || '').trim()) return;
    setRestoreTeamsLoading(true);
    try {
      const options = await getLscMemberTeamOptions(leagueId, {
        current_member_id: member.id,
        unclaimed_only: true,
      });
      setRestoreTeams(options.teams || []);
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not load teams.'));
      setRestoreTeams([]);
    } finally {
      setRestoreTeamsLoading(false);
    }
  };

  const handleRestore = async (teamLabel?: string) => {
    if (!restoreTarget) return;
    setRestoring(true);
    setError(null);
    try {
      await restoreLscMember(leagueId, restoreTarget.id, teamLabel);
      setRestoreTarget(null);
      setStatusFilter(DEFAULT_MEMBER_STATUS_FILTER);
      await loadMembers();
      await refreshAfterMutation();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not restore member.'));
    } finally {
      setRestoring(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setError(null);
    try {
      await removeLscMember(leagueId, removeTarget.id);
      setRemoveTarget(null);
      await loadMembers();
      await refreshAfterMutation();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not remove member.'));
    } finally {
      setRemoving(false);
    }
  };

  const filteredMembers = filterMembersByStatus(members, statusFilter);
  const rosterCounts = memberRosterCounts(members);
  const liveDetectionCopy = liveDetectionConnectionCopy(readiness.live_detection_connection);
  const membersPageHref = lscPath(`/leagues/${leagueId}/members`);

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="members-teams-step-panel">
      <LeagueSetupStepCard step={wizardStep} />

      <div className="rounded-lg border border-secondary-700 bg-secondary-900/30 px-4 py-3 text-sm text-secondary-300">
        {MEMBERS_SETUP_INTRO_COPY}
      </div>

      {!loading ? (
        <p className="text-sm text-secondary-300" data-testid="member-roster-counts">
          Imported, not yet claimed: {rosterCounts.importedUnclaimed} · Claimed:{' '}
          {rosterCounts.claimed} · Twitch linked: {rosterCounts.twitchLinked}
          {liveDetectionCopy ? ` · ${liveDetectionCopy}` : ''}
        </p>
      ) : null}

      {attentionItems.length > 0 ? (
        <div
          className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 space-y-2"
          data-testid="members-readiness-attention"
        >
          <p className="text-sm font-medium text-amber-100">Members setup needs attention</p>
          <ul className="list-disc list-inside text-sm text-amber-100/90 space-y-1">
            {attentionItems.map((item) => (
              <li key={item.key}>{item.description}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={membersPageHref} className="text-sm text-primary-300 hover:text-primary-200">
          {MEMBERS_FULL_PAGE_LINK_COPY}
        </Link>
        {isCommissioner && !leagueArchived ? (
          <button
            type="button"
            className="btn-primary text-sm min-h-11 px-4"
            onClick={openCreate}
            data-testid="setup-add-member-btn"
          >
            Add member
          </button>
        ) : null}
      </div>

      {loading ? <LoadingState message="Loading members…" /> : null}

      <LscMutationErrorAlert error={error} />

      {isCommissioner && !leagueHasNeonSportzImport(league) ? (
        <LeagueInvitePanel
          leagueId={leagueId}
          leagueName={league.name}
          leagueArchived={leagueArchived}
          unassignedJoin
        />
      ) : null}

      {isCommissioner ? (
        <>
          <JoinRequestQueuePanel
            leagueId={leagueId}
            leagueArchived={leagueArchived}
            hideWhenEmpty
            onDecision={() => {
              void loadMembers().then(refreshAfterMutation);
            }}
          />
          <ImportedMembersPromotionPanel
            leagueId={leagueId}
            onApplied={() => {
              void loadMembers().then(refreshAfterMutation);
            }}
          />
          <MemberClaimQueuePanel
            leagueId={leagueId}
            onDecision={() => {
              void loadMembers().then(refreshAfterMutation);
            }}
          />
        </>
      ) : null}

      {!loading ? (
        <>
          <MemberStatusFilterBar
            members={members}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          {isCommissioner ? (
            <TeamAssignmentConflictBanner
              conflictCount={memberCounts?.team_assignment_conflict_count ?? 0}
              visible
            />
          ) : null}
          {toast ? (
            <p className="text-sm text-emerald-300" role="status" data-testid="members-copy-toast">
              {toast}
            </p>
          ) : null}
          <MembersTable
            members={filteredMembers}
            ownerUid={league.commissioner_uid}
            canEdit={isCommissioner && !leagueArchived}
            onEdit={isCommissioner && !leagueArchived ? openEdit : undefined}
            onRemove={isCommissioner && !leagueArchived ? setRemoveTarget : undefined}
            onRestore={isCommissioner && !leagueArchived ? (member) => void openRestore(member) : undefined}
            pendingClaimMemberIds={isCommissioner ? pendingClaimMemberIds : undefined}
            memberInvites={isCommissioner ? memberInvites : undefined}
            onCopyInviteLink={isCommissioner && !leagueArchived ? (member) => void copyMemberLink(member) : undefined}
            onCopyInviteMessage={
              isCommissioner && !leagueArchived ? (member) => void copyMemberMessage(member) : undefined
            }
            onReinviteMember={isCommissioner && !leagueArchived ? (member) => void reinviteMember(member) : undefined}
            invitingMemberId={invitingMemberId}
            emptyMessage={emptyFilterMessage(statusFilter)}
            showInactiveHelp={statusFilter === 'inactive'}
          />
        </>
      ) : null}

      {isCommissioner ? (
        <>
          <MemberFormModal
            open={modalOpen}
            mode={modalMode}
            leagueId={leagueId}
            initial={editingMember}
            saving={saving}
            formError={formError}
            onClose={() => {
              setModalOpen(false);
              setFormError(null);
            }}
            onSubmit={handleSubmit}
          />
          <RemoveMemberConfirmModal
            open={Boolean(removeTarget)}
            member={removeTarget}
            removing={removing}
            onClose={() => setRemoveTarget(null)}
            onConfirm={() => void handleRemove()}
          />
          <RestoreMemberModal
            open={Boolean(restoreTarget)}
            member={restoreTarget}
            teams={restoreTeams}
            teamsLoading={restoreTeamsLoading}
            restoring={restoring}
            onClose={() => setRestoreTarget(null)}
            onConfirm={(teamLabel) => void handleRestore(teamLabel)}
          />
        </>
      ) : null}
    </div>
  );
};

export default MembersTeamsStepPanel;
